/**
 * The arithmetic and payload decisions behind the checkout context, without React.
 *
 * `contexts/CheckoutProvider.tsx` is a shell: twenty pieces of `useState`, four
 * refs, five `fetch` calls and a Stripe promise. Threaded through that shell are
 * the decisions that actually determine what the buyer is charged and what the
 * server is asked for — how a coupon, a shipping rate and a vehicle listing
 * combine into a subtotal, which body a payment-intent request becomes for an
 * authenticated buyer and for a guest, and which fields each funnel event
 * carries when the product is half-loaded.
 *
 * Inside the provider none of those could be reached without rendering a React
 * tree, stubbing six context providers, a Zod schema module and `fetch`, and
 * then reading the decision back out of a `JSON.stringify` argument. Here they
 * are called with plain objects and checked against plain values. That matters
 * most for the money: the coupon/vehicle/shipping combinations that decide
 * `subTotal` had one assertion between them, and every one of the `shippable`
 * coercions the server validates against was unreached.
 *
 * Everything moved verbatim from CheckoutProvider.tsx, which now calls back
 * into it. Nothing here fetches, renders, or reads global state — `getPartner()`
 * is still called by the provider and its answer passed in, so this file has no
 * window dependency.
 *
 * These builders describe the request; they never decide the price. The server
 * re-derives every amount in /api/checkout/create-payment-intent — the prices
 * here exist so the page can show the buyer what it is about to ask for.
 */

import { checkoutPriceBreakdown, type CheckoutPriceBreakdown } from './checkout/price-breakdown';
import type { Product, ShippingRate } from './checkout/checkout-types';
import type { CheckoutSelectedRate, CheckoutShippingResult } from '../schemas/tracking-events';

export type ShippingQuoteOutcome = CheckoutShippingResult['quote_outcome'];

/**
 * The variant fields this module reads. Declared locally rather than imported
 * from ProductProvider so a pure module does not depend on a React context.
 */
export interface SelectedVariantLike {
  vid: string;
  price: number;
}

// ─── Totals ──────────────────────────────────────────────────────────────────

export interface CheckoutTotalsInput {
  /** `product?.price` — falsy (missing product, zero price) zeroes the totals. */
  productPrice?: number | string | null;
  /**
   * The whole listing as /api/listings/[id] returned it, when the caller has
   * it. Its flat `couponId`/`couponDiscountAmount` keys are the listing's
   * automatic coupon; without them `productPrice` is taken as the price.
   */
  listing?: unknown;
  /** Vehicles arrange shipping separately, so their ship amount is never quoted. */
  isVehicleListing: boolean;
  /** `selectedShipping?.amount` for the rate the buyer has highlighted. */
  selectedShippingAmount?: number | string | null;
  /**
   * `selectedShipping?.tax?.taxCents`: the tax the server quoted with that
   * rate. Absent (an older response, or no rate) means no tax line.
   */
  selectedShippingTaxCents?: number | null;
  couponType: string;
  couponDiscount: number;
  couponCode?: string | null;
  couponApplicationMode: 'code' | 'auto' | '';
}

export interface CheckoutTotals {
  shipAmount: number;
  buyerDiscount: number;
  manualDiscount: number;
  effectiveItemPrice: number;
  subTotal: number;
  stripeFee: number;
  /** Dollars of sales tax, from the quote attached to the highlighted rate. */
  salesTax: number;
  totalPrice: number;
  /** Every row the order summary draws, from the same sum as the totals above. */
  breakdown: CheckoutPriceBreakdown;
}

/**
 * What the checkout page displays, from the item price, the highlighted rate
 * and whichever coupon is in play.
 *
 * The sum itself lives in utils/checkout/price-breakdown.ts, which runs it in
 * cents through the functions create-payment-intent bills with. This keeps the
 * provider's field names. A `seller` coupon never reduces the buyer's bill, and
 * an automatic coupon is already inside the listing's price, so neither is
 * subtracted here — hence `manualDiscount`, which is only ever a typed code.
 */
export function computeCheckoutTotals(input: CheckoutTotalsInput): CheckoutTotals {
  const breakdown = checkoutPriceBreakdown({
    listing: input.listing ?? { price: input.productPrice },
    isVehicleListing: input.isVehicleListing,
    shippingAmount: input.selectedShippingAmount,
    taxCents: input.selectedShippingTaxCents,
    coupon: {
      couponType: input.couponType,
      couponDiscount: input.couponDiscount,
      couponCode: input.couponCode,
      couponApplicationMode: input.couponApplicationMode,
    },
  });
  const buyerDiscount = input.couponType === 'buyer' ? input.couponDiscount : 0;

  return {
    shipAmount: breakdown.shipAmount,
    buyerDiscount,
    manualDiscount: breakdown.codeDiscount,
    effectiveItemPrice: breakdown.itemPrice,
    subTotal: breakdown.subTotal,
    stripeFee: breakdown.stripeFee,
    salesTax: breakdown.salesTax,
    totalPrice: breakdown.totalPrice,
    breakdown,
  };
}

/**
 * The rate at `index`, or null.
 *
 * `shipping` is a numeric index into the quoted rates and defaults to 0, so an
 * empty rate list indexes to `undefined` rather than out of range. The explicit
 * null/undefined guards are what keep `rates[index]` from throwing when the
 * index itself is missing.
 */
export function selectShippingRateAtIndex(
  rates: ShippingRate[],
  index: number | null | undefined,
): ShippingRate | null {
  return index !== null && index !== undefined && rates[index] ? rates[index] : null;
}

// ─── Payment-intent request bodies ───────────────────────────────────────────

export type PaymentIntentShippingRatePayload = {
  id: string;
  provider: string | ShippingRate['provider'];
  serviceName: string;
  amount: number;
  estimatedDays: number;
  shipmentId?: string;
  serviceToken?: string;
};

export type PaymentIntentListingPayload = {
  id: number;
  title: string;
  price: number;
  shippable: boolean;
  user?: { id: number };
};

export type PaymentIntentCouponFields = {
  couponId?: number;
  couponCode?: string | null;
};

export type AuthenticatedPaymentIntentBody = {
  mode: 'authenticated';
  listingId: number;
  paymentMethodId?: string;
  shippingAddressId?: number;
  /**
   * Only for a buyer whose account carries none — a phone-only signup. The
   * server prefers the address on the row and falls back to this one, which is
   * what it sends the receipt to. It is never written to the account: an email
   * is a login credential here, and /api/user/profile refuses to set one
   * without OTP for exactly that reason.
   */
  email?: string;
  listing: PaymentIntentListingPayload;
  selectedShippingRate?: PaymentIntentShippingRatePayload;
  variantVid?: string;
  /** Paid from the wallet buttons; see AuthPaymentIntentRequestSchema. */
  express?: boolean;
} & PaymentIntentCouponFields;

export type GuestPaymentIntentBody = {
  mode: 'guest';
  listingId: number;
  email: string;
  shippingAddress: Record<string, string>;
  listing: PaymentIntentListingPayload;
  selectedShippingRate?: PaymentIntentShippingRatePayload;
  variantVid?: string;
  partnerCode?: string;
  /** Paid from the wallet buttons; see GuestPaymentIntentRequestSchema. */
  express?: boolean;
} & PaymentIntentCouponFields;

/**
 * The listing's own shippable flag, as three shapes mean the same thing.
 *
 * Elasticsearch hands back `1`, the listings API hands back `true` and the
 * legacy PHP payload hands back `'1'`. The server rejects a payment intent
 * whose `shippable` disagrees with the row, so all three have to coerce.
 */
export function isShippableValue(shippable: boolean | number | string | undefined): boolean {
  return shippable === 1 || shippable === true || shippable === '1';
}

/**
 * The price to quote for a listing: the variant's when the buyer picked one.
 *
 * The server validates this against LISTINGS_METADATA.externalData.variants —
 * the value here is purely for client-side display continuity.
 */
export function resolveEffectiveListingPrice(
  listing: Product,
  selectedVariant: SelectedVariantLike | null | undefined,
): number {
  return selectedVariant ? selectedVariant.price : Number(listing.price);
}

/**
 * The rate the buyer picked, in the shape create-payment-intent re-verifies.
 *
 * `provider` falls back to the bare value because rates arrive both transformed
 * (an object carrying the carrier's name and logo) and raw (the carrier string).
 */
export function buildSelectedShippingRatePayload(
  shippingRate: ShippingRate | null | undefined,
): PaymentIntentShippingRatePayload | undefined {
  return shippingRate ? {
    id: shippingRate.id,
    provider: shippingRate.provider?.name || shippingRate.provider,
    serviceName: shippingRate.service,
    amount: Number(shippingRate.amount),
    estimatedDays: Number(shippingRate.estimatedDays),
    shipmentId: shippingRate.shipmentId,
    serviceToken: shippingRate.serviceToken,
  } : undefined;
}

/**
 * The coupon to send, if one may be sent at all.
 *
 * An auto-applied coupon rides along even when the coupon feature is off,
 * because the server resolved it from the listing rather than the buyer typing
 * it, and it still has a redemption to record.
 */
export function buildCouponFields(input: {
  couponsActive: boolean;
  couponApplicationMode: 'code' | 'auto' | '';
  couponId: number | null;
  couponCode: string | null;
}): PaymentIntentCouponFields {
  return (input.couponsActive || input.couponApplicationMode === 'auto') && input.couponId
    ? { couponId: input.couponId, couponCode: input.couponCode }
    : {};
}

export interface AuthenticatedPaymentIntentInput {
  listing: Product;
  paymentMethodId?: string;
  /** Typed into the checkout email field by a buyer whose account has none. */
  email?: string;
  shippingAddress?: { id?: number | string } | null;
  shippingRate?: ShippingRate | null;
  selectedVariant?: SelectedVariantLike | null;
  couponsActive: boolean;
  couponApplicationMode: 'code' | 'auto' | '';
  couponId: number | null;
  couponCode: string | null;
  /** Paid from the Express Checkout buttons, to the wallet's address (saved first). */
  express?: boolean;
}

/** The body POSTed to create-payment-intent for a signed-in buyer. */
export function buildAuthenticatedPaymentIntentBody(
  input: AuthenticatedPaymentIntentInput,
): AuthenticatedPaymentIntentBody {
  const { listing, shippingAddress, selectedVariant } = input;
  const variantVid = selectedVariant?.vid;

  return {
    mode: 'authenticated',
    listingId: Number(listing.id),
    paymentMethodId: input.paymentMethodId,
    shippingAddressId: shippingAddress?.id ? Number(shippingAddress.id) : undefined,
    listing: {
      id: Number(listing.id),
      title: String(listing.title),
      price: resolveEffectiveListingPrice(listing, selectedVariant),
      shippable: isShippableValue(listing.shippable),
      user: listing?.user?.id ? { id: Number(listing.user.id) } : undefined,
    },
    selectedShippingRate: buildSelectedShippingRatePayload(input.shippingRate),
    ...(variantVid ? { variantVid } : {}),
    ...(input.email ? { email: input.email } : {}),
    ...buildCouponFields(input),
    ...(input.express ? { express: true } : {}),
  };
}

export interface GuestPaymentIntentInput {
  product: Product;
  email: string;
  shippingAddress: Record<string, string>;
  shippingRate?: ShippingRate | null;
  selectedVariant?: SelectedVariantLike | null;
  couponsActive: boolean;
  couponApplicationMode: 'code' | 'auto' | '';
  couponId: number | null;
  couponCode: string | null;
  /**
   * `getPartner()`'s answer — read from `?p=` or localStorage by the provider so
   * attribution survives to checkout even when the partner_code cookie is gone.
   */
  partnerCode?: string | null;
  /** Paid from the Express Checkout buttons rather than the card form. */
  express?: boolean;
}

/** The body POSTed to create-payment-intent for a guest buyer. */
export function buildGuestPaymentIntentBody(
  input: GuestPaymentIntentInput,
): GuestPaymentIntentBody {
  const { product, selectedVariant } = input;
  const variantVid = selectedVariant?.vid;

  return {
    mode: 'guest',
    listingId: Number(product.id),
    email: input.email,
    shippingAddress: input.shippingAddress,
    listing: {
      id: Number(product.id),
      title: String(product.title),
      price: resolveEffectiveListingPrice(product, selectedVariant),
      shippable: isShippableValue(product.shippable),
      // Unlike the authenticated body this is unconditional: a guest checkout
      // with no seller on the listing must reach the server and be refused
      // there, not be silently posted without a seller.
      user: { id: Number(product?.user?.id) },
    },
    selectedShippingRate: buildSelectedShippingRatePayload(input.shippingRate),
    ...(variantVid ? { variantVid } : {}),
    ...buildCouponFields(input),
    ...(input.partnerCode ? { partnerCode: input.partnerCode } : {}),
    ...(input.express ? { express: true } : {}),
  };
}

// ─── Funnel + abandonment event payloads ─────────────────────────────────────

export type FunnelUserOptions = {
  userId?: number;
};

/** The `userId` option every checkout funnel event carries, when known. */
export function buildFunnelUserOptions(profileId?: number | string | null): FunnelUserOptions {
  return { userId: profileId ? Number(profileId) : undefined };
}

export type ShippingCalculatedEventData = {
  listing_id?: number;
  rate_count: number;
  lowest_rate?: number;
};

/**
 * What a completed rate quote reports.
 *
 * `lowest_rate` reads index 0 because `transformShippingRates` returns them
 * cheapest-first and the provider preselects that one.
 */
export function buildShippingCalculatedEventData(
  listingId: string | number | undefined,
  rates: ShippingRate[],
): ShippingCalculatedEventData {
  return {
    listing_id: listingId ? Number(listingId) : undefined,
    rate_count: rates.length,
    lowest_rate: rates.length > 0 ? rates[0].amount : undefined,
  };
}

const toCents = (dollars: number | string | null | undefined): number | undefined => {
  const n = Number(dollars);
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : undefined;
};

export interface ShippingQuoteResultInput {
  outcome: ShippingQuoteOutcome;
  rates?: ShippingRate[];
  /** The item price in dollars, as the product carries it. */
  itemPrice?: number | string | null;
  quoteMs?: number;
  error?: string;
}

/**
 * What the quote said, for the quote's own row and for an abandonment after it.
 * The cheapest rate is found, not assumed to be first: the share a buyer saw is
 * the whole point of the row.
 */
export function buildShippingQuoteResult(input: ShippingQuoteResultInput): CheckoutShippingResult {
  const rates = input.rates ?? [];
  const lowest = rates.reduce<ShippingRate | undefined>((low, r) => (!low || r.amount < low.amount ? r : low), undefined);
  const lowestCents = toCents(lowest?.amount);
  const itemCents = toCents(input.itemPrice);
  return {
    quote_outcome: input.outcome,
    quote_error: input.error,
    rate_count: rates.length,
    lowest_rate_cents: lowestCents,
    lowest_carrier: lowest?.provider?.name || undefined,
    lowest_service: lowest?.service || undefined,
    lowest_est_days: lowest && Number.isFinite(lowest.estimatedDays) ? lowest.estimatedDays : undefined,
    item_price_cents: itemCents,
    shipping_share: lowestCents !== undefined && itemCents ? Math.round((lowestCents / itemCents) * 100) / 100 : undefined,
    quote_ms: input.quoteMs !== undefined ? Math.max(0, Math.round(input.quoteMs)) : undefined,
  };
}

/** The rate selected now, for the abandonment a departure would send. */
export function buildSelectedRateFields(rate: ShippingRate | null | undefined): CheckoutSelectedRate | null {
  const cents = toCents(rate?.amount);
  if (!rate || cents === undefined) return null;
  return {
    selected_rate_cents: cents,
    selected_carrier: rate.provider?.name || undefined,
    selected_service: rate.service || undefined,
  };
}

export type CheckoutStartedEventData = {
  listing_id: number;
  listing_title?: string;
  listing_price?: number;
  seller_id?: number;
};

/**
 * The listing fields checkout entry reports.
 *
 * Passed field by field rather than as the whole product: the effect that emits
 * this event depends on exactly these fields, and handing it `product` would
 * widen that dependency to every unrelated change to the listing object.
 */
export interface CheckoutStartedListingFields {
  title?: string;
  price?: number;
  sellerId?: number | string;
}

/** What checkout entry reports. The listing id is required — see the provider. */
export function buildCheckoutStartedEventData(
  listingId: number | string,
  listing: CheckoutStartedListingFields,
): CheckoutStartedEventData {
  return {
    listing_id: Number(listingId),
    listing_title: listing.title || undefined,
    listing_price: listing.price ? Number(listing.price) : undefined,
    seller_id: listing.sellerId ? Number(listing.sellerId) : undefined,
  };
}

export type CheckoutStartedAbandonmentContext = {
  listing_id: number | string;
  user_id?: number | string;
  is_guest: 0 | 1;
};

/** The context abandonment tracking keeps for a checkout that may be left. */
export function buildCheckoutStartedAbandonmentContext(
  listingId: number | string,
  profileId: number | string | undefined,
  loggedIn: boolean,
): CheckoutStartedAbandonmentContext {
  return {
    listing_id: listingId,
    user_id: profileId,
    is_guest: loggedIn ? 0 : 1,
  };
}

export type ShippingSelectedAbandonmentContext = {
  listing_id?: number | string;
  user_id?: number | string;
  shipping_cost?: number;
};

/** The context abandonment tracking advances to once a rate is chosen. */
export function buildShippingSelectedAbandonmentContext(
  product: Product | null,
  profileId: number | string | undefined,
  selectedRate: ShippingRate | null | undefined,
): ShippingSelectedAbandonmentContext {
  return {
    listing_id: product?.id,
    user_id: profileId,
    shipping_cost: selectedRate?.amount,
  };
}

export type ShippingSelectedEventData = {
  checkout_session_id: string;
  listing_id?: number;
  listing_title?: string;
  listing_price?: number;
  category_id?: number;
  category_group_id?: number;
  shipping_method?: string;
  shipping_cost?: number;
};

/** What leaving the shipping step reports. */
export function buildShippingSelectedEventData(
  product: Product | null,
  selectedRate: ShippingRate | null | undefined,
  checkoutSessionId: string,
): ShippingSelectedEventData {
  return {
    checkout_session_id: checkoutSessionId,
    listing_id: product?.id ? Number(product.id) : undefined,
    listing_title: product?.title || undefined,
    listing_price: product?.price ? Number(product.price) : undefined,
    category_id: product?.categoryId ? Number(product.categoryId) : undefined,
    category_group_id: product?.categoryGroupId ? Number(product.categoryGroupId) : undefined,
    shipping_method: selectedRate?.service || undefined,
    shipping_cost: selectedRate?.amount ? Number(selectedRate.amount) : undefined,
  };
}

/**
 * Which payment UI made the attempt.
 *
 * `guest_card_form` is its own value rather than reusing `card_form`: the two
 * forms take different intents (a guest's is manual-capture and creates the
 * account) and it is the guest number that nobody could see. Every production
 * `payment_attempted` row to date carries `is_guest = 0`, because the guest
 * form was the one path that emitted nothing at all.
 */
export type PaymentMethodSource = 'saved_card' | 'card_form' | 'guest_card_form' | 'express';

export type PaymentAttemptedEventData = {
  checkout_session_id: string;
  listing_id?: number;
  listing_title?: string;
  listing_price?: number;
  payment_method_source: PaymentMethodSource;
  /**
   * What the buyer actually paid with inside that source: `card`, `cashapp`,
   * `apple_pay`, `google_pay`, `link`. The source alone could not tell a
   * closed Cash App QR code from a 3DS challenge -- both end `requires_action`.
   */
  payment_method_type?: string;
};

/** How a paid checkout was paid, for `complete`. */
export interface PaidMethod {
  source: PaymentMethodSource;
  type?: string;
  isGuest?: boolean;
}

/**
 * The listing fields a payment event reports.
 *
 * Narrower than `Product` on purpose — the same reason
 * `CheckoutStartedListingFields` is: the guest form holds a three-field listing
 * of its own, not the provider's `Product`, and demanding the full type here
 * would mean casting a real object into a shape it never had.
 */
export interface PaymentEventListingFields {
  id?: number | string | null;
  title?: string | null;
  price?: number | string | null;
}

/**
 * What submitting a payment reports.
 *
 * Deliberately the same four keys whichever form sent it, so the Payment
 * Attempted milestone counts one kind of thing however the shopper paid, plus
 * `payment_method_source` to tell them apart when that is the question.
 */
export function buildPaymentAttemptedEventData(
  product: PaymentEventListingFields | null,
  checkoutSessionId: string,
  source: PaymentMethodSource,
): PaymentAttemptedEventData {
  return {
    checkout_session_id: checkoutSessionId,
    listing_id: product?.id ? Number(product.id) : undefined,
    listing_title: product?.title || undefined,
    listing_price: product?.price ? Number(product.price) : undefined,
    payment_method_source: source,
  };
}

export type PaymentFailedEventData = PaymentAttemptedEventData & {
  /** Where the attempt died: our own checks, or Stripe's answer. */
  failure_stage: 'validation' | 'intent' | 'stripe';
  /** Stripe's machine-readable code (`card_declined`, ...), when it gave one. */
  error_code?: string;
  /** Stripe's decline detail (`insufficient_funds`, ...), when it gave one. */
  decline_code?: string;
  /** Stripe's error family (`card_error`, `validation_error`, ...). */
  error_type?: string;
};

/**
 * What a payment that did not go through reports.
 *
 * The counterpart to `payment_attempted`, and the reason it exists: an attempt
 * with no completion beside it could mean a decline, a validation refusal, or a
 * shopper who closed the tab mid-form, and the funnel had no way to tell those
 * apart. Card failures were written to `/api/errors/log` as errors, which is a
 * different table with a different session namespace — it could never be joined
 * to the checkout the failure happened in.
 *
 * The codes are Stripe's own and carry no card data: `card_declined`,
 * `insufficient_funds`, `expired_card`. Never the PAN, name or address.
 */
export function buildPaymentFailedEventData(
  attempt: PaymentAttemptedEventData,
  failure: {
    /** `intent`: our own server refused to make the payment (price moved, listing gone). */
    stage: 'validation' | 'intent' | 'stripe';
    errorCode?: string | null;
    declineCode?: string | null;
    errorType?: string | null;
  },
): PaymentFailedEventData {
  return {
    ...attempt,
    failure_stage: failure.stage,
    error_code: failure.errorCode || undefined,
    decline_code: failure.declineCode || undefined,
    error_type: failure.errorType || undefined,
  };
}

export type CheckoutCompleteEventData = ShippingSelectedEventData & {
  payment_intent_id?: string;
  total_amount?: number;
  /** Which path paid: express, the guest card form, a saved card, a new card. */
  payment_method_source?: PaymentMethodSource;
  /** What paid inside it: card, cashapp, apple_pay, google_pay, link. */
  payment_method_type?: string;
  is_guest?: boolean;
};

/**
 * What a paid checkout reports.
 *
 * `total_amount` is the item plus the chosen rate. It is a report of what was
 * charged, never an input to the charge — the server derived that already.
 */
export function buildCheckoutCompleteEventData(
  product: Product | null,
  selectedRate: ShippingRate | null | undefined,
  checkoutSessionId: string,
  paymentIntentId?: string,
  method?: PaidMethod,
): CheckoutCompleteEventData {
  return {
    checkout_session_id: checkoutSessionId,
    payment_intent_id: paymentIntentId || undefined,
    payment_method_source: method?.source,
    payment_method_type: method?.type || undefined,
    is_guest: method?.isGuest,
    listing_id: product?.id ? Number(product.id) : undefined,
    listing_title: product?.title || undefined,
    listing_price: product?.price ? Number(product.price) : undefined,
    category_id: product?.categoryId ? Number(product.categoryId) : undefined,
    category_group_id: product?.categoryGroupId ? Number(product.categoryGroupId) : undefined,
    shipping_method: selectedRate?.service || undefined,
    shipping_cost: selectedRate?.amount ? Number(selectedRate.amount) : undefined,
    total_amount: product?.price
      ? Number(product.price) + (selectedRate?.amount ? Number(selectedRate.amount) : 0)
      : undefined,
  };
}

// ─── Error shaping ───────────────────────────────────────────────────────────

/**
 * The Error the tracker is given for a value that may not be one.
 *
 * A rejected `fetch` in Safari can reject with a string or a DOM exception from
 * another realm, and `trackError` reads `.stack` off whatever it gets.
 */
export function toTrackedError(error: unknown, fallbackMessage: string): Error {
  return error instanceof Error ? error : new Error(fallbackMessage);
}

/** The sentence shown for a thrown value, when only a message is wanted. */
export { errorMessageOr } from './error-message';
